/**
 * Палитра линий: насыщенные цвета (HSV S=100%, шаг только по яркости V), чтобы на схеме и в разговоре
 * были «красная / синяя / ярко-жёлтая», а не пастель. Подписи для стандартного ряда — слово + hex.
 */

export type LineColorSwatch = { color: string; title: string }

function swatch(color: string, title?: string): LineColorSwatch {
  return { color, title: title ?? color.toUpperCase() }
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n))
}

/** H,S,V ∈ [0,1]; H для градусов 0–360. */
function hsvToHex(hDeg: number, s: number, v: number): string {
  const H = ((((hDeg % 360) + 360) % 360) / 60)
  const S = clamp01(s)
  const V = clamp01(v)
  const i = Math.floor(H)
  const f = H - i
  const p = V * (1 - S)
  const q = V * (1 - S * f)
  const t = V * (1 - S * (1 - f))
  let r = 0
  let g = 0
  let b = 0
  switch (i) {
    case 0:
      r = V
      g = t
      b = p
      break
    case 1:
      r = q
      g = V
      b = p
      break
    case 2:
      r = p
      g = V
      b = t
      break
    case 3:
      r = p
      g = q
      b = V
      break
    case 4:
      r = t
      g = p
      b = V
      break
    default:
      r = V
      g = p
      b = q
  }

  const toHex = (n: number) =>
    Math.round(clamp01(n) * 255)
      .toString(16)
      .padStart(2, '0')

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

const GRAY_ROW: LineColorSwatch[] = [
  swatch('#000000', 'Чёрный · #000000'),
  swatch('#434343', '#434343'),
  swatch('#666666', '#666666'),
  swatch('#999999', '#999999'),
  swatch('#b7b7b7', '#B7B7B7'),
  swatch('#cccccc', '#CCCCCC'),
  swatch('#d9d9d9', '#D9D9D9'),
  swatch('#efefef', '#EFEFEF'),
  swatch('#f3f3f3', '#F3F3F3'),
  swatch('#ffffff', 'Белый · #FFFFFF'),
]

/** 10 равномерных оттенков по кругу (красный → … → пурпурный). */
const COLUMN_HUES = [0, 36, 72, 108, 144, 180, 216, 252, 288, 324]

/** Всегда S=100%, только затемняем V — узнаваемый оттенок колонки сохраняется. */
const VALUE_STEPS = [1, 0.9, 0.8, 0.68, 0.56, 0.44, 0.32]

function buildVividGrid(): LineColorSwatch[] {
  const out: LineColorSwatch[] = []
  for (const value of VALUE_STEPS) {
    for (const hue of COLUMN_HUES) {
      const hex = hsvToHex(hue, 1, value)
      out.push(swatch(hex))
    }
  }
  return out
}

/** Серая строка + 7×10 насыщенных тонов. Hover показывает title (hex). */
export const LINE_SHEET_PALETTE: LineColorSwatch[] = [...GRAY_ROW, ...buildVividGrid()]

/**
 * Крупный ряд «как канцелярский маркер»: читается с экрана и по названию + hex в подсказке.
 * Жёлтый не #FFFF00 — на белой схеме тонула бы глазницу.
 */
export const LINE_STANDARD_SWATCHES: LineColorSwatch[] = [
  swatch('#000000', 'Чёрный · #000000'),
  swatch('#ffffff', 'Белый · #FFFFFF'),
  swatch('#d50000', 'Красный · #D50000'),
  swatch('#2962ff', 'Синий · #2962FF'),
  swatch('#fdd835', 'Жёлтый · #FDD835'),
  swatch('#00c853', 'Зелёный · #00C853'),
  swatch('#ff6d00', 'Оранжевый · #FF6D00'),
  swatch('#aa00ff', 'Фиолетовый · #AA00FF'),
]

export const DEFAULT_LINE_STROKE_HEX = '#3b82f6'
